import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { handleCallback, initiateLogin } from '@/modules/auth';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function processCallback() {
      const result = await handleCallback(searchParams);

      if (cancelled) return;

      if (result.success) {
        navigate(result.returnPath || '/', { replace: true });
      } else {
        setError(result.error || 'Authentication failed');
        setIsProcessing(false);
      }
    }

    processCallback();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  async function handleRetry() {
    setError(null);
    setIsProcessing(true);
    try {
      await initiateLogin();
    } catch {
      setError('Failed to initiate login. Please try again.');
      setIsProcessing(false);
    }
  }

  if (isProcessing) {
    return (
      <main id="main-content" className="flex min-h-screen items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div
            className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-border border-t-brand-600"
            aria-hidden="true"
          />
          <h1 className="text-xl font-semibold text-content">
            Completing authentication...
          </h1>
          <p className="mt-2 text-sm text-content-secondary">
            Please wait while we verify your credentials.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="flex min-h-screen items-center justify-center">
      <div className="max-w-md text-center" role="alert" aria-live="assertive">
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10"
          aria-hidden="true"
        >
          <svg
            className="h-6 w-6 text-danger"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-content">
          Authentication Failed
        </h1>
        <p className="mt-2 text-sm text-content-secondary">
          {error}
        </p>
        <button
          type="button"
          onClick={handleRetry}
          className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-content-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          Try again
        </button>
      </div>
    </main>
  );
}
